import { Commodity, Contract, Process, ProcessSpecification, Segment } from "@/types/contract";
import { Product } from "@/types/enumerations";
import { CustomerTypeValues } from "@/constants/customer";
import { BundleServiceId } from "@/constants/supplyPoint";
import type { Deposit } from "@/requests/contract/deposits";

export const getProducts = (contract: Contract, productEnums: Product[], usePreviousCustomerProduct: boolean) => {
  const currentProduct = productEnums.find(p => p.id === contract.contractDetails?.productId);
  let products =
    contract.process === Process.PS ? getProductsForProcessPS(contract, productEnums) : getBaseProducts(contract, productEnums);

  if (contract.supplyPoint.commodity === Commodity.Gas) {
    products = filterProductsByBundleService(contract, products);
  }

  const previousCustomerProducts = products.filter(p => p.fictive);
  const newProducts = products.filter(p => !p.fictive);

  if (contract.process === Process.PS) {
    products = usePreviousCustomerProduct
      ? previousCustomerProducts
      : currentProduct
      ? addCurrentProduct(newProducts, currentProduct)
      : newProducts;
  }

  return { products, previousCustomerProducts };
};

const filterProductsByBundleService = (contract: Contract, products: Product[]) => {
  if (contract.process === Process.PS) {
    const isHeatingSelected = contract.supplyPoint.sop?.tdS01 === true;

    //removal of product "Variant" 0000010051 when heating is selected and kettle brand is manually selected - ECTRO-409
    if (isHeatingSelected && isKettleBrandManuallySelected(contract)) {
      products = products
        .filter(p => p.fictive || p.bundleServices.includes(BundleServiceId) || p.bundleServices.length === 0)
        .filter(p => p.value.disId !== "0000010051");
    }

    return products.filter(p =>
      p.fictive
        ? true
        : isKettleBrandManuallySelected(contract)
        ? p.bundleServices.includes(BundleServiceId) || p.bundleServices.length === 0
        : p.bundleServices.length === 0
    );
  }

  return products.filter(p =>
    isKettleBrandManuallySelected(contract) ? p.bundleServices.includes(BundleServiceId) : p.bundleServices.length === 0
  );
};

const getBaseProducts = (contract: Contract, products: Product[]) => {
  return products.filter(
    p =>
      p.processes.includes(contract.process) &&
      p.value.relevance === "online" &&
      p.value.commodity === contract.supplyPoint.commodity &&
      p.value.customerType === (contract.customer.customerType as string)
  );
};

const getProductsForProcessPS = (contract: Contract, products: Product[]) => {
  const psProducts = products.filter(
    p =>
      p.processes.includes(contract.process) &&
      p.value.commodity === contract.supplyPoint.commodity &&
      p.value.customerType === (contract.customer.customerType as string) &&
      (p.segment === contract.segment || p.segment === Segment.ALL)
  );

  return filterProductsByUrlChannelAndFictiveConditions(contract, psProducts);
};

const addCurrentProduct = (products: Product[], currentProduct: Product) => {
  const isCurrentProductIncluded = products.find(p => p.id === currentProduct.id);
  const isPreviousCustomerProduct = currentProduct.fictive;

  return isCurrentProductIncluded || isPreviousCustomerProduct ? products : [currentProduct].concat(products);
};

// eslint-disable-next-line sonarjs/cognitive-complexity
const filterProductsByUrlChannelAndFictiveConditions = (contract: Contract, products: Product[]) => {
  const previousProductBundleService = contract.previousProduct?.bundleService;
  const segmentB2B = contract.segment === Segment.B2B;
  const processSpecificationOnline = contract.processSpecification === ProcessSpecification.ONLINE;
  const processSpecificationAdhoc = contract.processSpecification === ProcessSpecification.ADHOC;
  const processReasonDeath = contract.supplyPoint.previousContract?.processReason === "02";

  let withFictive = false;
  if (!segmentB2B && ((processSpecificationOnline && processReasonDeath) || processSpecificationAdhoc)) {
    withFictive = true;
  }

  return products
    .filter(product => {
      if (
        processReasonDeath &&
        contract.customer.customerType === CustomerTypeValues.Household &&
        contract.oldCustomer?.customerType === CustomerTypeValues.Household
      ) {
        return true;
      } else {
        return !product.fictive;
      }
    })
    .filter(product => {
      const isUrlChannelProduct = urlChannelProducts.includes(product.id);
      const urlCondition = !!contract.sellChannel === isUrlChannelProduct;

      let fictiveCondition = true;
      let previousProductBundleServiceString = "[]";

      if (withFictive) {
        fictiveCondition = true;

        if (product.fictive) {
          if (previousProductBundleService !== null) {
            previousProductBundleServiceString = JSON.stringify([previousProductBundleService]);
          }

          fictiveCondition = JSON.stringify(product.bundleServices) === previousProductBundleServiceString;
        }
      } else {
        fictiveCondition = product.fictive === false;
      }
      return isUrlChannelProduct ? urlCondition : fictiveCondition;
    });
};

const urlChannelProducts = [
  "00000211552",
  "00000211521",
  "00000211511",
  "00000211502",
  "00000211501",
  "00000211494",
  "00000211492",
  "00000211491",
];

// special use case when only brand of kettle is selected by user, we have to select appropriate products with kettle bundle service
export const isKettleBrandManuallySelected = (contract: Contract) => {
  return contract.supplyPoint.kettleBrand !== "other" && contract.supplyPoint.kettleBrand !== null;
};

/**
 * Converts the raw description HTML into a `<li>`-based list.
 * If a paragraph contains the `#gift#|#arrows#|#lock#|#clock#|#like#` token, the token is removed
 * and the resulting `<li>` gets a corresponding data attribute so CSS can render the
 * appropriate icon in place of the default list marker.
 */
export const formatDescription = (html: string): string =>
  html
    .replaceAll("<p>", "<li>")
    .replaceAll("</p>", "</li>")
    .replace(/<li>([\s\S]*?)<\/li>/g, (match, inner: string) =>
      inner.includes("#gift#") ? `<li data-gift>${inner.replaceAll("#gift#", "").trim()}</li>` : match
    )
    .replace(/<li>([\s\S]*?)<\/li>/g, (match, inner: string) =>
      inner.includes("#arrows#") ? `<li data-reload>${inner.replaceAll("#arrows#", "").trim()}</li>` : match
    )
    .replace(/<li>([\s\S]*?)<\/li>/g, (match, inner: string) =>
      inner.includes("#lock#") ? `<li data-lock>${inner.replaceAll("#lock#", "").trim()}</li>` : match
    )
    .replace(/<li>([\s\S]*?)<\/li>/g, (match, inner: string) =>
      inner.includes("#clock#") ? `<li data-clock>${inner.replaceAll("#clock#", "").trim()}</li>` : match
    )
    .replace(/<li>([\s\S]*?)<\/li>/g, (match, inner: string) =>
      inner.includes("#like#") ? `<li data-like>${inner.replaceAll("#like#", "").trim()}</li>` : match
    );

export const getDepositPriceByProductId = (deposits: Deposit[] | undefined, productId: string | number): number | undefined => {
  const value = deposits?.find(deposit => deposit.productId === String(productId))?.depositRecommendation;
  if (value === null || value === undefined) {
    return undefined;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};
